import React, { useState } from "react";
import Icon from "components/AppIcon";

const variables = [
  { key: "rentGrowth", label: "Rent Growth Rate", unit: "%", min: -2, max: 10, step: 0.5, base: 3.5 },
  { key: "appreciation", label: "Property Appreciation", unit: "%", min: 0, max: 12, step: 0.5, base: 4.2 },
  { key: "vacancyRate", label: "Vacancy Rate", unit: "%", min: 0, max: 20, step: 0.5, base: 5.0, inverse: true },
  { key: "interestRate", label: "Interest Rate", unit: "%", min: 3, max: 10, step: 0.25, base: 6.75, inverse: true },
  { key: "expenseGrowth", label: "Expense Growth", unit: "%", min: 0, max: 8, step: 0.5, base: 2.5, inverse: true },
];

const computeImpact = (variable, value) => {
  const delta = value - variable?.base;
  const multiplier = variable?.inverse ? -1 : 1;
  return (delta * multiplier * 0.8)?.toFixed(2);
};

export default function SensitivityAnalysis() {
  const [values, setValues] = useState(
    Object.fromEntries(variables?.map((v) => [v?.key, v?.base]))
  );

  const handleChange = (key, val) => {
    setValues((prev) => ({ ...prev, [key]: Number(val) }));
  };

  const totalImpact = variables?.reduce((sum, v) => {
    return sum + parseFloat(computeImpact(v, values?.[v?.key]));
  }, 0);

  const baseROI = 7.8;
  const adjustedROI = (baseROI + totalImpact)?.toFixed(2);

  return (
    <div className="card-base flex flex-col gap-5">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <h3 className="font-heading font-semibold text-base text-foreground">
            Sensitivity Analysis
          </h3>
          <p className="text-xs font-caption text-muted-foreground mt-0.5">
            Adjust key variables to see ROI impact
          </p>
        </div>
        <div className="flex items-center gap-3">
          <div className="text-right">
            <p className="text-xs font-caption text-muted-foreground">Adjusted ROI</p>
            <p
              className={`text-xl font-mono-data font-bold ${
                parseFloat(adjustedROI) >= baseROI ? "text-success" : "text-accent"
              }`}
            >
              {adjustedROI}%
            </p>
          </div>
          <div
            className={`flex items-center gap-1 text-sm font-caption font-semibold px-2 py-1 rounded-lg ${
              totalImpact >= 0 ? "bg-success/10 text-success" : "bg-accent/10 text-accent"
            }`}
          >
            <Icon name={totalImpact >= 0 ? "TrendingUp" : "TrendingDown"} size={14} color="currentColor" />
            {totalImpact >= 0 ? "+" : ""}{totalImpact?.toFixed(2)}%
          </div>
        </div>
      </div>
      <div className="flex flex-col gap-4">
        {variables?.map((v) => {
          const impact = parseFloat(computeImpact(v, values?.[v?.key]));
          const pct = ((values?.[v?.key] - v?.min) / (v?.max - v?.min)) * 100;
          return (
            <div key={v?.key} className="flex flex-col gap-2">
              <div className="flex items-center justify-between">
                <label className="text-sm font-caption font-medium text-foreground">
                  {v?.label}
                </label>
                <div className="flex items-center gap-3">
                  <span className="text-sm font-mono-data font-semibold text-foreground">
                    {values?.[v?.key]}{v?.unit}
                  </span>
                  <span
                    className={`text-xs font-caption font-semibold px-1.5 py-0.5 rounded ${
                      impact >= 0 ? "bg-success/10 text-success" : "bg-accent/10 text-accent"
                    }`}
                  >
                    {impact >= 0 ? "+" : ""}{impact}%
                  </span>
                </div>
              </div>
              <div className="relative">
                <input
                  type="range"
                  min={v?.min}
                  max={v?.max}
                  step={v?.step}
                  value={values?.[v?.key]}
                  onChange={(e) => handleChange(v?.key, e?.target?.value)}
                  className="w-full accent-primary h-2 rounded-full cursor-pointer"
                />
                <div className="flex justify-between text-xs font-caption text-muted-foreground mt-1">
                  <span>{v?.min}{v?.unit}</span>
                  <span className="text-xs text-muted-foreground">Base: {v?.base}{v?.unit}</span>
                  <span>{v?.max}{v?.unit}</span>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}